/**
 *
 * @license Royal Canin
 * @contributors :
 *  - Amar YASSA
 */

import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ofType, Actions, createEffect } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { filter, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import {
  generateNewRecommendationRandomId,
  requestGenerateNewRecommendationRandomId,
  setShareRecommendation,
  setTargetWeightForWLP,
} from './share-recommendation.actions';
import { AppRoutes } from '@app/shared/utils/enums/routes.enum';
import { Helper, Tool } from '@app/shared/utils';
import { Store } from '@ngrx/store';
import { selectRecommendationId, selectShareRecommendationData } from '@app/store/share-recommendation/share-recommendation.selector';
import { selectToolInfo } from '@app/store/core';
import { selectWeight } from '../patient-profile/patient-profile.selectors';
import { setPatientWeight } from '../patient-profile';
import { getExpectedNextVisit } from '../consultation/consultation.actions';
import { selectExpectedNextVisit } from '../consultation/consultation.selectors';

@Injectable({ providedIn: 'root' })
export class ShareRecommendationEffects {
  setShareRecommendation$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(setShareRecommendation),
        tap(() => {
          this.router.navigate([AppRoutes.SHARE_RECOMMENDATION]);
        }),
      );
    },
    { dispatch: false },
  );

  generateRandomRecommendationId$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(requestGenerateNewRecommendationRandomId),
      concatLatestFrom(({ productIds }) => [this.store$.select(selectRecommendationId(productIds)), this.store$.select(selectToolInfo)]),
      filter(([, recommendationId]) => !recommendationId),
      map(([{ productIds }, , toolInfo]) => {
        const id = [...productIds].sort().join('');
        const randomId = Helper.randomId();
        return generateNewRecommendationRandomId({ id, flowId: toolInfo.tool_flow_id, randomId });
      }),
    );
  });

  setTargetWeightForWLP$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setTargetWeightForWLP),
      withLatestFrom(
        this.store$.select(selectWeight),
        this.store$.select(selectShareRecommendationData),
        this.store$.select(selectExpectedNextVisit),
      ),
      switchMap(([{ targetWeight }, weight, shareRecommendationData, expectedNextVisit]) => [
        setPatientWeight({ weight: { ...weight, targetWeight: targetWeight }, tool: Tool.RapidReco }),
        setShareRecommendation({
          data: { ...shareRecommendationData, petInfo: { ...shareRecommendationData.petInfo, targetWeight: targetWeight } },
        }),
        getExpectedNextVisit({
          value: { ...shareRecommendationData.petInfo, targetWeight: targetWeight },
          nextDate: expectedNextVisit.intermediateVisit.weightDate,
        }),
      ]),
    ),
  );

  constructor(
    private store$: Store,
    private actions$: Actions,
    private router: Router,
  ) {}
}
